// noinspection ES6PreferShortImport
// noinspection SuspiciousTypeOfGuard

import { RemoveTrailingS, SnakeToCamelCase, SnakeToPascalCase } from "../types/general";
import { SnakeCase } from "type-fest";
import { isBrowser, isEdgeRuntime } from "#/utils/environment";
import { default as safeStringifyLib } from "json-stringify-safe";
import CryptoJS from "crypto-js";
import { createHmac } from "crypto";

export const hashData = (
  data: string, key: string
): string => {
  if (isBrowser() || isEdgeRuntime()) {
    return CryptoJS.HmacSHA256(data, key).toString();
  } else {
    const hmac = createHmac("sha256", key); // Create HMAC instance with SHA256 and the key
    hmac.update(data); // Add the data to be hashed
    return hmac.digest("hex");
  }
};

export const hyphenToCamelCase = (hyphenString: string): string => {
  // Split the string by hyphens
  const parts = hyphenString.split("-");

  // First part stays as is
  const firstPart = parts[0];

  // For each subsequent part, capitalize the first letter
  const camelParts = parts.slice(1).map(part =>
    part.charAt(0).toUpperCase() + part.slice(1)
  );

  // Join all parts together
  return firstPart + camelParts.join("");
};

export const safeStringify = (obj: any, space?: number): string => {
  function replacer(_: string, value: any): any {
    // Test if value is a class instance
    if (
      typeof value === "object"
      && value !== null
      && value.constructor
      && value.constructor !== Object // Not a plain object
      && value.constructor !== Array // Not an array
    ) {
      const instanceObj: any = {};
      // Get all own properties (enumerable and non-enumerable)
      Object.getOwnPropertyNames(value).forEach((prop) => {
        instanceObj[prop] = value[prop];
      });
      // Optionally include inherited properties if not present (e.g., 'name' for Error)
      if (!instanceObj.name && value.name) {
        instanceObj.name = value.name;
      }
      return instanceObj;
    }
    return value; // Let json-stringify-safe handle circular refs and other cases
  }

  return safeStringifyLib(obj, replacer, space);
};

export function assert(condition: any, message?: string): asserts condition {
  if (!condition) {
    throw new Error(`${message}\nThis should never happen.` || "Assertion failed.\nThis should never happen.");
  }
}

type SleepKind = "milliseconds" | "seconds" | "minutes" | "millisecond" | "second" | "minute" | "hours" | "hour" | "ms" | "s" | "sec" | "min" | "m" | "h";
export const sleep = (
  amount: number,
  kind: SleepKind = "milliseconds"
): Promise<void> => {
  let ms: number;
  switch (kind) {
    case "milliseconds":
    case "millisecond":
    case "ms":
      ms = amount;
      break;
    case "seconds":
    case "second":
    case "s":
    case "sec":
      ms = amount * 1000;
      break;
    case "minutes":
    case "minute":
    case "min":
    case "m":
      ms = amount * 1000 * 60;
      break;
    case "hours":
    case "hour":
    case "h":
      ms = amount * 1000 * 60 * 60;
      break;
    default:
      throw new Error(`Invalid kind: ${kind}`);
  }
  return new Promise((r) => setTimeout(r, ms));
};


export const snakeCaseToCamelCase = <T extends string>(str: T): SnakeToCamelCase<T> =>
  str.replaceAll(
    /(?!^)_(.)/g,
    (_, char) => char.toUpperCase()
  ) as SnakeToCamelCase<T>;

export const snakeCaseToPascalCase = <T extends string>(str: T): SnakeToPascalCase<T> =>
  capitalize(snakeCaseToCamelCase(str));

export const pascalCaseToSnakeCase = <T extends string>(pascal: T): SnakeCase<T> =>
  pascal.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase() as SnakeCase<T>;

export const removePlural = <T extends string>(str: T): RemoveTrailingS<T> =>
  str.replace(/s$/, "") as RemoveTrailingS<T>;

export const capitalize = <T extends string>(str: T): Capitalize<T> =>
  `${str.charAt(0).toUpperCase()}${str.slice(1)}` as Capitalize<T>;

const _title = (str: string, transformAllUppercase = false): string => {
  if (typeof str !== "string") {
    return str;
  }
  const words = str.split(/\s+/);
  const isConstantStr = str.toUpperCase() === str;
  const titledWords = words.map(word => {
    if (!isConstantStr && word === word.toUpperCase() && !transformAllUppercase) {
      return word; // if word is all uppercase, return as is
    }
    const firstLetter = word.charAt(0).toUpperCase();
    const restOfWord = word.slice(1).toLowerCase();
    return firstLetter + restOfWord;
  });
  return titledWords.join(" ");
};
export const titleAllUppercase = (str: string): string => _title(str, true);
export const title = (str: string): string => _title(str, false);

/** "obj" is the first type given instead of the second one, if "condition" is true, or is a function resolving to true.  */
export const isType1 = <T1, T2>(obj: T1 | T2, condition: boolean | ((obj: T1 | T2) => boolean)): obj is T1 => {
  if (typeof condition !== "function") {
    return condition;
  }
  return condition(obj);
};

export const isNanSafe = (value: any): boolean => {
  if (isBrowser()) {
    return isNaN(Number(value));
  } else {
    return Number.isNaN(Number(value));
  }
};

export const exceedsMaxSafeInt = (value: number): boolean => {
  const cleanedMoneyString = Number(String(value).replace(/[^(0-9)]/g, ""));
  if (isNanSafe(cleanedMoneyString)) {
    throw new Error(`Received: ${cleanedMoneyString} which is not a number.`);
  }
  return BigInt(cleanedMoneyString) > BigInt(Number.MAX_SAFE_INTEGER);
};

export const isPromise = <T>(value: any): value is Promise<T> =>
  Boolean(value) // Ensure that value's truthiness is true
  && typeof value.then === "function";

export const deArrayify = <T>(item?: T | T[] | null): T => {
  if (item == null) {
    return item as T;
  } else if (!Array.isArray(item)) {
    return item;
  } else if (item.length !== 1) {
    throw new Error(`Expected array of length 1 to use deArrayify, but got length ${item.length}`);
  }
  return item[0];
};

export const isString = (value: any): value is string => typeof value === "string";
export const isNumber = (value: any): value is number => typeof value === "number";
export const isBoolean = (value: any): value is boolean => typeof value === "boolean";
export const isFunction = (value: any): value is Function => typeof value === "function";
export const isObject = (value: any): value is object => typeof value === "object";
export const isArray = (value: any): value is any[] => Array.isArray(value);

export const getCWD = (): string => {
  // const env = process.env;
  return (!process.env.VERCEL_ENV || process.env.VERCEL_ENV === "development") ? require("app-root-path").path : process.cwd();
};


// Helper to chunk array
export function chunkArray<T>(arr: T[], size: number): T[][] {
  return Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, i * size + size)
  );
}
